/**
 * FreeHome.jsx — Lane 4 (Feb 2026).
 *
 * The two-button walled-garden home for signed-in FREE users. Rendered
 * from Dashboard.jsx at the top of the body when `!isPaidUser(user)`.
 * Paid users never see this component — the paid dashboard renders
 * exactly as today.
 *
 * Two large primary destinations:
 *   - My Free Lessons → /my-free-lessons (Lane 3B mini-library)
 *   - Classroom Support → /classroom-support (public support hub)
 *
 * A small quota strip surfaces the monthly-generate + saved-cap gauges
 * pulled from GET /api/free-lesson/quota so the user always knows
 * where they stand. Everything else — paid pages, planner, gradebook,
 * report cards — is reachable only via the LeftRail's locked items,
 * which route to /pricing (see LeftRail.jsx Lane 4 change).
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, BookOpen, HeartHandshake, Sparkles } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { fetchFreeLessonQuota } from "../lib/freeLessonApi";
import AppShell from "./shell/AppShell";
import FeaturedLessonCard from "./FeaturedLessonCard";
import ReportCardNudgeBanner from "./ReportCardNudgeBanner";

const FreeHome = () => {
  const { user } = useAuth();
  const [quota, setQuota] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetchFreeLessonQuota()
      .then((q) => {
        if (!cancelled) setQuota(q);
      })
      .catch(() => {
        /* fail-open: home renders without the strip */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const firstName = (user?.name || "").split(" ")[0] || "there";

  return (
    <AppShell title="Home" subtitle="Your free tier">
      <div
        data-testid="free-home"
        className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14"
      >
      {/* Report-card seasonal nudge — self-hides when the admin switch
          is Off, or when the auth-user is an admin (double-guard). */}
      {!user?.is_admin ? (
        <div className="mb-6 sm:mb-8">
          <ReportCardNudgeBanner audience="free" />
        </div>
      ) : null}

      {/* Greeting */}
      <div>
        <p className="text-[10px] sm:text-[11px] font-extrabold uppercase tracking-[0.22em] text-[#4a6984]/70">
          Your Free Tier
        </p>
        <h1 className="mt-2 font-black text-3xl sm:text-4xl tracking-tight text-[#1c2d3d]">
          Welcome back, {firstName}.
        </h1>
        <p className="mt-3 text-base sm:text-lg text-[#4a6984]/85 font-medium leading-relaxed max-w-2xl">
          Two things to do on the free plan — build a lesson from scratch, or grab a
          research-backed plan for a student who needs a boost.
        </p>
      </div>

      {/* Quota strip */}
      {quota && !quota.unlimited ? (
        <div
          data-testid="free-home-quota-strip"
          className="mt-6 flex flex-wrap gap-2"
        >
          <span className="inline-flex items-center gap-1.5 rounded-full bg-[#f4f5f7] border border-[#4a6984]/15 px-3 py-1 text-[11px] font-extrabold uppercase tracking-[0.14em] text-[#4a6984]">
            <Sparkles className="h-3.5 w-3.5" />
            {quota.remaining} of {quota.limit} generations left this month
          </span>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-[#f4f5f7] border border-[#4a6984]/15 px-3 py-1 text-[11px] font-extrabold uppercase tracking-[0.14em] text-[#4a6984]">
            <BookOpen className="h-3.5 w-3.5" />
            {quota.saved_used ?? 0} of {quota.saved_limit ?? 15} saved
          </span>
        </div>
      ) : null}

      {/* Featured Lesson (only renders when admin has pinned one) */}
      <FeaturedLessonCard />

      {/* Two big buttons */}
      <div className="mt-8 sm:mt-10 grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
        <Link
          to="/my-free-lessons"
          data-testid="free-home-cta-my-free-lessons"
          className="group relative rounded-2xl border border-[#4a6984]/15 bg-white p-6 sm:p-8 shadow-sm hover:shadow-md hover:border-[#96ded1] transition-all"
        >
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-[#96ded1]/20 text-[#1c2d3d]">
                <Sparkles className="h-5 w-5" strokeWidth={2.2} />
              </span>
              <h2 className="mt-4 font-black text-xl sm:text-2xl text-[#1c2d3d] tracking-tight">
                My Free Lessons
              </h2>
              {/* Lane 5 fix (Feb 2026) — library-flavoured copy with
                  the live saved count so a teacher scanning the
                  dashboard immediately knows this is where their
                  saved lessons live, not the generator. */}
              <p className="mt-2 text-sm sm:text-base text-[#4a6984]/85 font-medium leading-relaxed">
                {quota && !quota.unlimited
                  ? `${quota.saved_used ?? 0} of ${quota.saved_limit ?? 15} saved. `
                  : ""}
                Reopen, reprint, or make a new one.
              </p>
            </div>
            <ArrowRight className="h-5 w-5 text-[#4a6984]/50 group-hover:text-[#1c2d3d] transition-colors shrink-0" />
          </div>
        </Link>

        <Link
          to="/classroom-support"
          data-testid="free-home-cta-classroom-support"
          className="group relative rounded-2xl border border-[#4a6984]/15 bg-white p-6 sm:p-8 shadow-sm hover:shadow-md hover:border-[#96ded1] transition-all"
        >
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-[#e6a63a]/20 text-[#1c2d3d]">
                <HeartHandshake className="h-5 w-5" strokeWidth={2.2} />
              </span>
              <h2 className="mt-4 font-black text-xl sm:text-2xl text-[#1c2d3d] tracking-tight">
                Classroom Support
              </h2>
              <p className="mt-2 text-sm sm:text-base text-[#4a6984]/85 font-medium leading-relaxed">
                Practical, research-grounded plans for ADHD, Autism, Giftedness, and
                2e students — free for every teacher.
              </p>
            </div>
            <ArrowRight className="h-5 w-5 text-[#4a6984]/50 group-hover:text-[#1c2d3d] transition-colors shrink-0" />
          </div>
        </Link>
      </div>

      {/* Trial nudge */}
      <div className="mt-10 sm:mt-14 rounded-2xl border border-[#4a6984]/15 bg-[#f4f5f7] p-5 sm:p-6">
        <p className="text-sm font-extrabold uppercase tracking-[0.14em] text-[#4a6984]/75">
          Ready for the full engine?
        </p>
        <p className="mt-2 text-sm sm:text-base text-[#1c2d3d] font-medium leading-relaxed">
          Slide decks, full differentiation, unit plans, planner, report cards, community
          library — all unlocked on a 7-day free trial.
        </p>
        <Link
          to="/pricing"
          data-testid="free-home-cta-trial"
          className="mt-4 inline-flex items-center gap-1.5 rounded-md bg-[#1c2d3d] px-5 py-2.5 text-xs font-extrabold uppercase tracking-[0.14em] text-white hover:bg-[#132030]"
        >
          Start 7-day trial
          <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      </div>
      </div>
    </AppShell>
  );
};

export default FreeHome;
